/**
 * Team day view of My Calendar (owner ask 2026-10-16): ONE day, one column per
 * recruiter, side by side, on a shared time axis, so who is free when reads at
 * a glance. Which recruiters get a column is the page's recruiter selection
 * (the picker, or the chips above the grid); the day is stepped by the page's
 * arrows. Layout math lives in teamDayLogic.ts.
 *
 * What a column shows: that recruiter's callbacks (cyan) and tasks (amber);
 * YOUR OWN personal events with their titles; a teammate's booking-blocking
 * events only as grey dashed "Busy" blocks (no title: GET /calendar
 * ?include_busy=true sends none); and an out-of-office day as a dashed band
 * in the all-day row with the column greyed. Clicking an item does what it
 * does elsewhere on the page (a callback or task opens its lead; your event
 * opens its editor). Clicking an empty half hour in YOUR column starts a new
 * event there; a teammate's column takes no clicks (their calendar is theirs).
 *
 * The grid scrolls inside its card both ways, so the names stay pinned above
 * and the hours pinned to the left however many recruiters are shown.
 */
import { useEffect, useMemo, useState } from 'react';
import { UserMinus } from 'lucide-react';
import { CalendarItem, UserAbsence } from '../../types';
import { clockTime } from '../../lib';
import { personColor } from './colors';
import { absenceKindLabel } from './AbsenceModal';
import { outcomeDotCls } from './CallbackOutcome';
import {
  MIN_BLOCK_MIN,
  TeamColumn,
  gridHours,
  hourLabel,
  itemsForColumn,
  layoutLanes,
  slotAt,
  splitDay,
} from './teamDayLogic';

interface Props {
  day: Date;
  columns: TeamColumn[];
  /** The day's items (recurring events already expanded, kind filters applied). */
  items: CalendarItem[];
  /** The day's out-of-office records (the whole team; matched to columns by user). */
  absences: UserAbsence[];
  /** Whether the viewer can add an event (their own column takes clicks). */
  canCreate: boolean;
  isPhone: boolean;
  chipClass: (it: CalendarItem) => string;
  isStruck: (it: CalendarItem) => boolean;
  onItemClick: (it: CalendarItem) => void;
  onCreateAt: (start: Date) => void;
  onAbsenceClick: (a: UserAbsence) => void;
}

const BUSY_CLS =
  'border border-dashed border-gray-400 bg-gray-100 text-gray-600 dark:border-gray-500 dark:bg-gray-700/60 dark:text-gray-300';

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

function blockTitle(it: CalendarItem): string {
  const time = it.all_day ? 'All day' : `${clockTime(it.start)}–${clockTime(it.end)}`;
  return `${time} · ${it.title}${it.outcome ? ` · ${it.outcome.label}` : ''}`;
}

export default function TeamDayView({
  day,
  columns,
  items,
  absences,
  canCreate,
  isPhone,
  chipClass,
  isStruck,
  onItemClick,
  onCreateAt,
  onAbsenceClick,
}: Props) {
  const pxPerHour = isPhone ? 52 : 64;
  const colMin = isPhone ? '8rem' : '9.5rem';

  // The red now-line moves once a minute while the day shown is today.
  const [now, setNow] = useState(() => new Date());
  const isToday = sameDay(day, now);
  useEffect(() => {
    if (!isToday) return undefined;
    const t = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(t);
  }, [isToday]);

  const perColumn = useMemo(
    () =>
      columns.map((col) => {
        const { allDay, timed } = splitDay(itemsForColumn(items, col.id), day);
        const out = col.id ? absences.filter((a) => a.user_id === col.id) : [];
        return { col, allDay, timed, placed: layoutLanes(timed), out };
      }),
    [columns, items, absences, day]
  );

  const { startHour, endHour } = useMemo(() => gridHours(perColumn.flatMap((c) => c.timed)), [perColumn]);
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const bodyHeight = hours.length * pxPerHour;
  const hasAllDayRow = perColumn.some((c) => c.allDay.length > 0 || c.out.length > 0);
  const gridCols = `3.5rem repeat(${Math.max(columns.length, 1)}, minmax(${colMin}, 1fr))`;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const showNow = isToday && nowMin >= startHour * 60 && nowMin <= endHour * 60;

  if (columns.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400">
        Pick at least one recruiter to show.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="max-h-[calc(100vh-15rem)] min-h-[20rem] overflow-auto" data-team-day>
        <div className="grid" style={{ gridTemplateColumns: gridCols }}>
          {/* Names, pinned to the top while the hours scroll. */}
          <div className="sticky left-0 top-0 z-30 border-b border-r border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800" />
          {perColumn.map(({ col, out }) => (
            <div
              key={`h-${col.id ?? 'unassigned'}`}
              className="sticky top-0 z-20 flex min-w-0 items-center gap-1.5 border-b border-r border-gray-200 bg-gray-50 px-2 py-2 dark:border-gray-700 dark:bg-gray-800"
              title={col.name}
            >
              <span className={`h-2 w-2 flex-shrink-0 rounded-full ${personColor(col.id).dot}`} />
              <span className="min-w-0 truncate text-sm font-medium text-gray-800 dark:text-gray-100">
                {col.name}
                {col.isMe && <span className="ml-1 text-xs font-normal text-gray-400">(you)</span>}
              </span>
              {out.length > 0 && (
                <span className="ml-auto flex-shrink-0 rounded border border-dashed border-gray-400 px-1 text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:border-gray-500 dark:text-gray-400">
                  Out
                </span>
              )}
            </div>
          ))}

          {/* All day: out-of-office bands and all-day items. */}
          {hasAllDayRow && (
            <>
              <div className="sticky left-0 z-10 flex items-start justify-end border-b border-r border-gray-200 bg-white px-1.5 py-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500">
                All day
              </div>
              {perColumn.map(({ col, allDay, out }) => (
                <div
                  key={`a-${col.id ?? 'unassigned'}`}
                  className="min-w-0 space-y-1 border-b border-r border-gray-200 p-1 dark:border-gray-700"
                >
                  {out.map((a) => (
                    <button
                      key={`abs-${a.id}`}
                      type="button"
                      onClick={() => onAbsenceClick(a)}
                      title={`${a.user_name || 'Recruiter'} · ${absenceKindLabel(a.kind)}${a.note ? ` · ${a.note}` : ''}`}
                      className="flex w-full items-center gap-1 rounded border border-dashed border-gray-400 bg-gray-100 px-1.5 py-0.5 text-left text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-500 dark:bg-gray-700/60 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <UserMinus className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">Out · {absenceKindLabel(a.kind)}</span>
                    </button>
                  ))}
                  {allDay.map((it) => (
                    <button
                      key={`ad-${it.source_kind}-${it.source_id}-${it.instance_key ?? ''}`}
                      type="button"
                      onClick={() => onItemClick(it)}
                      disabled={it.source_kind === 'busy'}
                      title={blockTitle(it)}
                      className={`flex w-full items-center truncate rounded px-1.5 py-0.5 text-left text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-default ${
                        it.source_kind === 'busy' ? BUSY_CLS : chipClass(it)
                      } ${isStruck(it) ? 'line-through opacity-60' : ''}`}
                    >
                      <span className="truncate">{it.title}</span>
                    </button>
                  ))}
                </div>
              ))}
            </>
          )}

          {/* Hours, pinned to the left while the columns scroll. */}
          <div className="sticky left-0 z-10 border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800" style={{ height: bodyHeight }}>
            {hours.map((h, i) => (
              <div
                key={h}
                className="absolute right-1.5 -translate-y-1/2 text-[11px] tabular-nums text-gray-400 dark:text-gray-500"
                style={{ top: i * pxPerHour }}
              >
                {i === 0 ? '' : hourLabel(h)}
              </div>
            ))}
          </div>

          {perColumn.map(({ col, placed, out }) => {
            const mine = col.isMe && canCreate;
            return (
              <div
                key={`c-${col.id ?? 'unassigned'}`}
                className={`relative min-w-0 border-r border-gray-200 dark:border-gray-700 ${
                  out.length > 0 ? 'bg-gray-50 dark:bg-gray-900/40' : ''
                }`}
                style={{ height: bodyHeight }}
              >
                {/* Half-hour rows: lines for everyone, click targets in your own column. */}
                {hours.flatMap((h, i) =>
                  [0, 30].map((m) => {
                    const top = i * pxPerHour + (m ? pxPerHour / 2 : 0);
                    const line = m
                      ? 'border-t border-dotted border-gray-100 dark:border-gray-700/60'
                      : 'border-t border-gray-200 dark:border-gray-700';
                    if (!mine) {
                      return <div key={`${h}:${m}`} className={`absolute inset-x-0 ${line}`} style={{ top, height: pxPerHour / 2 }} />;
                    }
                    const at = slotAt(day, startHour, i * 60 + m);
                    return (
                      <button
                        key={`${h}:${m}`}
                        type="button"
                        onClick={() => onCreateAt(at)}
                        aria-label={`New event at ${at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`}
                        className={`absolute inset-x-0 cursor-pointer transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 dark:hover:bg-primary-900/20 ${line}`}
                        style={{ top, height: pxPerHour / 2 }}
                      />
                    );
                  })
                )}

                {placed.map((p) => {
                  const it = p.item;
                  const top = ((p.startMin - startHour * 60) / 60) * pxPerHour;
                  const height = (Math.max(p.endMin - p.startMin, MIN_BLOCK_MIN) / 60) * pxPerHour;
                  const busy = it.source_kind === 'busy';
                  const short = height < 40;
                  return (
                    <button
                      key={`${it.source_kind}-${it.source_id}-${it.instance_key ?? ''}`}
                      type="button"
                      onClick={() => onItemClick(it)}
                      disabled={busy}
                      title={blockTitle(it)}
                      className={`absolute z-[1] overflow-hidden rounded px-1.5 text-left text-xs leading-tight shadow-sm ring-1 ring-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-default dark:ring-gray-800 ${
                        busy ? BUSY_CLS : chipClass(it)
                      } ${isStruck(it) ? 'opacity-60' : ''} ${short ? 'flex items-center gap-1 py-0' : 'py-0.5'}`}
                      style={{
                        top: top + 1,
                        height: Math.max(height - 2, 14),
                        left: `calc(${(p.lane / p.lanes) * 100}% + 2px)`,
                        width: `calc(${100 / p.lanes}% - 4px)`,
                      }}
                    >
                      <span className="flex min-w-0 items-center gap-1">
                        {it.outcome && (
                          <span
                            aria-label={it.outcome.label}
                            className={`h-2 w-2 flex-shrink-0 rounded-full ring-1 ring-white dark:ring-gray-800 ${outcomeDotCls(it.outcome)}`}
                          />
                        )}
                        {/* A short block sharing its row gives the name the room; the time is in the tooltip. */}
                        {!(short && p.lanes > 1) && (
                          <span className="flex-shrink-0 tabular-nums opacity-75">{clockTime(it.start)}</span>
                        )}
                        {short && <span className={`truncate font-medium ${isStruck(it) ? 'line-through' : ''}`}>{it.title}</span>}
                      </span>
                      {!short && (
                        <span className={`block font-medium ${isStruck(it) ? 'line-through' : ''}`}>{it.title}</span>
                      )}
                    </button>
                  );
                })}

                {showNow && (
                  <div
                    className="pointer-events-none absolute inset-x-0 z-0 border-t-2 border-red-500"
                    style={{ top: ((nowMin - startHour * 60) / 60) * pxPerHour }}
                    aria-hidden="true"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
